/**
 * Helper: General
 * -----------------------------------------------------------------------------
 * General helper utility functions.
 * - Functions which don't fit into any of the other utils files.
 * - Add in alphabetical order.
 *
 */

import JSON5 from 'json5'
import { formatCurrencyCode } from '~/helpers/format'

/**
 * Debounce functions.
 * - Calls a function when a user hasn't carried out an event for a specific
 *   amount of time.
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null

  return function debounceFunc(...args) {
    const callNow = immediate && !timeout

    function later() {
      timeout = null
      if (!immediate) {
        callback.apply(this, args)
      }
    }

    window.clearTimeout(timeout)
    timeout = window.setTimeout(later, wait)

    if (callNow) {
      callback.apply(this, args)
    }
  }
}

/**
 * Fetch template view that returns JSON data.
 * @param endpoint
 * @returns {Promise<{}|any>}
 */
export async function fetchJsonView(endpoint) {
  try {
    const response = await fetch(endpoint)
    const dataText = await response.text()
    const element = document.createElement('div')
    element.innerHTML = dataText

    return JSON5.parse(element.innerHTML)
  } catch (error) {
    cnvs.ReportError(`Failed to load endpoint ${endpoint}`, error)
    return {}
  }
}

/**
 * Generate UUID
 * @returns {string}
 */
export function generateUUID() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2)
}

/**
 * Get metafield by namespace nad key from an array of metafields.
 * Can provide a namespace and key separately or a string in dot notation.
 * @param {String} [options.metaString] - Metafield namespace.key in string
 * format
 * @param {String} [options.namespace] - Metafield namespace
 * @param {String} [options.key] - Metafield key
 * @param {Boolean} [options.fullData] - Return the entire metafield object
 * @param {[Metafield]} options.metafields - Array of shopify metafields
 * @returns {*}
 */
export function getMetafield(options = {}) {
  const config = {
    metafields: [],
    fullData: false,
    ...getMetafieldIdFromString(options.metaString),
    ...options,
  }

  const { namespace, key, metafields, fullData } = config

  const metafield = metafields?.find((meta) => {
    if (!meta) {
      return false
    }
    return meta.key === key && meta.namespace === namespace
  })

  return fullData ? metafield : metafield?.value || metafield
}

/**
 * Generate a namespace and key object from a namespace and key in string
 * dot notation.
 * @param {String} metaString - Namespace and key in dot notation. e.g.
 * product_details.metal
 * @returns {{namespace: (*|string), key: (*|string)}}
 */
export function getMetafieldIdFromString(metaString = '') {
  const parts = metaString.split('.')
  const namespace = parts[0]?.trim() || ''
  const key = parts[1]?.trim() || ''
  return { namespace, key }
}

export function getMoneyFormat() {
  return `${formatCurrencyCode()} ${cnvs.store.preferredMoneyFormat}`
}

/**
 * Handles pluralisation of strings based on Unicode plural rules.
 * - https://unicode-org.github.io/cldr-staging/charts/latest/supplemental/language_plural_rules.html
 * - Follows English plural rules.
 * - Zero + (One|Single) + (Two + Few) + (Other|Many|Plural).
 * @param {Object} strings - Strings object containing locale strings.
 * @param {Number} number - Number to use for plural rules.
 * @returns {String}
 */
export function handlePluralisation(strings, number) {
  if (strings.zero && number === 0) {
    return strings.zero
  }

  const one = strings.one || strings.single

  if (one && number === 1) {
    return one
  }

  if (strings.two && number === 2) {
    return strings.two
  }

  const many = strings.other || strings.many || strings.plural

  if (strings.few) {
    if (many && strings.two && number === 3) {
      return strings.few
    }

    if (!many && strings.two && number > 2) {
      return strings.few
    }

    if (!many && number > 1) {
      return strings.few
    }
  }

  return many
}

/**
 * Test if a URL is a Shopify CDN URL.
 * - Returns true if URL contains:
 * -- cdn.shopify.com/
 * -- [custom-domain].[tld]/cdn/
 * -- [store].myshopify.com/cdn/
 * @param {String} url - URL to test.
 * @returns {Boolean}
 */
export function isCdnUrl(url) {
  const cdnUrls = [
    'cdn.shopify.com/',
    `${cnvs.store.shopDomain.replaceAll('/', '')}/cdn/`,
    `${cnvs.store.permanentDomain.replaceAll('/', '')}/cdn/`,
  ]

  return cdnUrls.some((cdnUrl) => url?.includes(cdnUrl))
}

/**
 * Test if a part of an element is in the viewport.
 * @param {HTMLElement} element - Element to check.
 * @param {Object} config - Set partial and direction configurations
 * @returns {Boolean} - True if element is in viewport.
 */
export function isVisible(element, config) {
  const options = {
    direction: 'y',
    partial: true,
    ...config || {},
  }
  const windowHeight = window.innerHeight
  const windowWidth = window.innerWidth

  const { top, bottom, left, right, width } = element.getBoundingClientRect()

  const topInView = (top > 0) && (top <= windowHeight)
  const bottomInView = (bottom > 0) && (bottom <= windowHeight)

  if (options.direction === 'y') {
    return options.partial ? topInView || bottomInView : topInView && bottomInView
  }

  const leftInView = options.partial ? left + width >= 0 : left >= 0
  const rightInView = options.partial ? left <= windowWidth : right <= windowWidth
  return leftInView && rightInView
}

/**
 * Replace character at index in the given string.
 * @param {string} str - String to modify
 * @param {number} index - Index of the character to replace
 * @param {string} replacement - The new replacement character(s)
 * @returns {string}
 */
export function replaceAt(str, index, replacement) {
  return str.substring(0, index) + replacement + str.substring(index + 1)
}

/**
 * Scrolls page smoothly to element.
 * @param {HTMLElement} element - Element to scroll to.
 * @param {Object} data - Additional data.
 * @param {Function} data.callback - Function run after completion.
 * @param {String} data.headerState - State of header when function is run.
 * @param {HTMLElement} data.page - Element to scroll.
 */
export function scrollToElement(element, { callback, headerState, page = window } = {}) {
  let viewportFromPageTop = window.pageYOffset

  if (page !== window) {
    viewportFromPageTop = 0
  }

  /**
   * Get element scroll position.
   */
  const elementRectangle = element.getBoundingClientRect()
  const elementFromViewportTop = Math.floor(elementRectangle.top)
  let position = viewportFromPageTop + elementFromViewportTop

  /**
   * Retrieve variables.
   */
  const headerHeight = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--header-group-height')
      .replace('px', ''),
  )

  const fontSize = Number(
    getComputedStyle(document.documentElement).fontSize
      .replace('px', ''),
  )

  let spacingM = Number(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--spacing-m')
      .replace('rem', ''),
  )

  spacingM *= fontSize

  /**
   * Offset height of header and update position.
   * - Assumes that when header is collapsed it has a height of 0.
   * - If it doesn't then update each condition with a new variable.
   * - Don't use --header-visible-height as that isn't updated in time.
   */
  const threshold = headerState === 'collapsed'
    ? spacingM
    : headerHeight + spacingM

  if (elementFromViewportTop === threshold) {
    return
  }

  const dynamicHeaderHeight = elementFromViewportTop > threshold
    ? 0
    : headerHeight

  position -= dynamicHeaderHeight
  position -= spacingM

  /**
   * Disable smooth scroll if prefers reduced motion.
   */
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth'

  page.scroll({
    behavior,
    left: 0,
    top: position,
  })

  if (typeof callback === 'function') {
    callback()
  }
}

/**
 * Set intersection observers to detect when add to cart button leaves viewport.
 * @param {Object} data - Data object.
 * @param {Function} data.callback - Function to run when intersecting.
 * @param {HTMLElement} data.element - Element to set observer on.
 * unobserved, must be a Vue element.
 * @param {String} [data.rootMargin] - Values to grow or shrink each side of the
 * root element's bounding box before computing intersections.
 * @param {Number|Array} [data.threshold] - Intersection observer threshold, 0.5
 * means the intersection is triggered when 50% of the element is in view.
 * @returns {Function}
 */
export function setIntersectionObserver(data = {}) {
  const { element, callback } = data
  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0.5,
    ...data,
  }

  const observer = new IntersectionObserver(callback, options)
  observer.observe(element)

  return observer
}

/**
 * Shuffle array
 * @param {Array} arr
 * @returns {[]}
 */
export function shuffle(arr) {
  /* eslint-disable */
  const array = arr.slice();
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  /* eslint-enable */
  return array
}

/**
 * Throttle functions.
 * - Calls a function at intervals of a specified amount of time while the user
 *   is still carrying out the event.
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout = null
  let initialCall = true

  return function throttleFunc(...args) {
    const callNow = immediate && initialCall

    function next() {
      callback.apply(this, args)
      timeout = null
    }

    if (callNow) {
      initialCall = false
      next()
    }

    if (!timeout) {
      timeout = window.setTimeout(next, wait)
    }
  }
}

/**
 * Unescape html entities
 * @param string
 * @returns {string}
 */
export function unescapeHtmlEntities(string) {
  return string
    .replace(/&#39;/g, '\'')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&colon;/g, ':')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
}
